import {
  Box,
  Card,
  FormControl,
  Grid,
  MenuItem,
  Select,
  Typography,
  Button,
  Switch,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  InputLabel,
} from '@mui/material';
import {
  Description,
  Schedule,
  Warning,
  Handshake,
  Add,
  Search,
  TrendingUp,
  TrendingDown,
  Remove,
  BusinessCenter,
} from '@mui/icons-material';
import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
/** AG-grid Import */
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { getColumnDefs } from './CUL_BASIC_ColumnDefs';
import CompanyDetailModal from './CompanyDetailModal';
import { ContractStatusLegendPanel } from './CUL_BASIC_StatusLegend';

const formatNumber = (num) => Number(num || 0).toLocaleString('ko-KR');

/** 만료까지 남은 일수 — remainingDays 우선, 없으면 contractEndDate(한글·ISO 등) 파싱 */
const getContractRemainingDays = (c) => {
  if (!c) return null;
  if (c.remainingDays !== undefined && c.remainingDays !== null) {
    const n = Number(c.remainingDays);
    if (Number.isFinite(n)) return n;
  }
  if (!c.contractEndDate) return null;
  const dateStr = String(c.contractEndDate)
    .replace(/년/g, '-')
    .replace(/월/g, '-')
    .replace(/일/g, '')
    .replace(/\s+/g, '')
    .split('-')
    .map((part, idx) => (idx === 0 ? part : part.padStart(2, '0')))
    .join('-');
  const endDate = new Date(dateStr);
  if (Number.isNaN(endDate.getTime())) return null;
  return Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
};

/** 계약 관리 화면 — 기업용 톤 */
const theme = {
  pageBg: 'linear-gradient(165deg, #e8ecf2 0%, #f1f4f8 42%, #f4f6fa 100%)',
  surface: '#ffffff',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  borderStrong: '1px solid rgba(15, 23, 42, 0.12)',
  text: { primary: '#0f172a', secondary: '#475569', muted: '#64748b' },
  primary: { main: '#1e3a5f', hover: '#152a45' },
  shadow: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.06)',
    md: '0 4px 24px rgba(15, 23, 42, 0.07)',
  },
  radius: { card: 2, control: 1 },
};

function CUL_BASIC(props) {
  const {
    contracts,
    filters,
    setFilters,
    onExport,
    onAddContract,
    onViewDetail,
    companyDetailData,
    onEditContract,
  } = props;

  const totalContracts = contracts?.totalContracts ?? 0;
  const totalCompanyChangeRate = contracts?.totalCompanyChangeRate ?? 0;
  const expiringSoon = contracts?.expiringSoon ?? 0;
  const expiringChangeRate = contracts?.expiringChangeRate ?? 0;
  const riskyAccounts = contracts?.riskyAccounts ?? 0;
  const riskAccountChangeRate = contracts?.riskAccountChangeRate ?? 0;
  const averageRenewalRate = contracts?.averageRenewalRate ?? 0;
  const averageRenewalChangeRate = contracts?.averageRenewalChangeRate ?? 0;
  const contractList = contracts?.contractList ?? [];

  const [searchText, setSearchText] = useState('');
  const [probabilityFilter, setProbabilityFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [showRiskyOnly, setShowRiskyOnly] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [gridPageSize, setGridPageSize] = useState(10);
  const [gridCurrentPage, setGridCurrentPage] = useState(0);
  const gridRef = useRef(null);

  /** 헤더 + 행 + 페이징 패널 높이 (현재 페이지 행 수에 맞춤) */
  const CUL_GRID_HEADER_HEIGHT = 49;
  const CUL_GRID_ROW_HEIGHT = 48;
  const CUL_GRID_PAGINATION_HEIGHT = 49;

  // 회사 상세 보기 핸들러
  const handleViewDetailInternal = useCallback(
    (companyData) => {
      // 외부에서 전달받은 onViewDetail 호출 (Search2 실행)
      if (onViewDetail) {
        onViewDetail(companyData);
      }
      // 모달도 열기
      setSelectedCompany(companyData);
      setDetailModalOpen(true);
    },
    [onViewDetail]
  );

  // 모달 닫기 핸들러
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedCompany(null);
  };

  // 컬럼 정의
  const columnDefs = useMemo(
    () => getColumnDefs(handleViewDetailInternal),
    [handleViewDetailInternal]
  );

  // 증감률 포맷팅 함수
  const formatChangeRate = (rate) => {
    const numRate = Number(rate);
    if (numRate === 0) return '0%';
    const sign = numRate > 0 ? '+' : '';
    return `${sign}${numRate.toFixed(1)}%`;
  };

  // 증감률에 따른 색상 결정
  const getChangeRateColor = (rate) => {
    const numRate = Number(rate);
    if (numRate > 0) return '#0d9488';
    if (numRate < 0) return '#c2410c';
    return theme.text.muted;
  };

  // 증감률에 따른 배경색 결정
  const getChangeRateBg = (rate) => {
    const numRate = Number(rate);
    if (numRate > 0) return 'rgba(13,148,136,0.12)';
    if (numRate < 0) return 'rgba(194,65,12,0.1)';
    return 'rgba(100,116,139,0.1)';
  };

  const cards = [
    {
      label: '총 계약 건수',
      value: formatNumber(totalContracts),
      icon: <Description sx={{ fontSize: 22 }} />,
      accent: '#1e4976',
      trendText: formatChangeRate(totalCompanyChangeRate),
      trendColor: getChangeRateColor(totalCompanyChangeRate),
      trendBg: getChangeRateBg(totalCompanyChangeRate),
    },
    {
      label: '곧 만료됨 (30일)',
      value: formatNumber(expiringSoon),
      icon: <Schedule sx={{ fontSize: 22 }} />,
      accent: '#b45309',
      trendText: formatChangeRate(expiringChangeRate),
      trendColor: getChangeRateColor(expiringChangeRate),
      trendBg: getChangeRateBg(expiringChangeRate),
    },
    {
      label: '위험 계정',
      value: formatNumber(riskyAccounts),
      icon: <Warning sx={{ fontSize: 22 }} />,
      accent: '#b91c1c',
      trendText: formatChangeRate(riskAccountChangeRate),
      trendColor: getChangeRateColor(riskAccountChangeRate),
      trendBg: getChangeRateBg(riskAccountChangeRate),
    },
    {
      label: '평균 갱신 확률',
      value: `${averageRenewalRate.toFixed(1)}%`,
      icon: <Handshake sx={{ fontSize: 22 }} />,
      accent: '#0f766e',
      trendText: formatChangeRate(averageRenewalChangeRate),
      trendColor: getChangeRateColor(averageRenewalChangeRate),
      trendBg: getChangeRateBg(averageRenewalChangeRate),
    },
  ];

  const filteredContracts = useMemo(() => {
    let filtered = [...contractList];

    // 검색 필터
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          (c.companyName || '').toLowerCase().includes(searchLower) ||
          (c.companyId?.toString() || '').includes(searchText)
      );
    }

    // 갱신 확률 필터
    if (probabilityFilter !== 'all') {
      filtered = filtered.filter((c) => {
        const rate = Number(c.renewalRate || 0);
        if (probabilityFilter === 'high') return rate > 70;
        if (probabilityFilter === 'medium') return rate >= 30 && rate <= 70;
        if (probabilityFilter === 'low') return rate < 30;
        return true;
      });
    }

    // 만료 기간 필터
    if (expiryFilter !== 'all') {
      filtered = filtered.filter((c) => {
        const diffDays = getContractRemainingDays(c);
        if (diffDays === null) return false;
        if (expiryFilter === '30days') return diffDays <= 30 && diffDays >= 0;
        if (expiryFilter === 'quarter') return diffDays > 30 && diffDays <= 90;
        if (expiryFilter === 'year') return diffDays > 90 && diffDays <= 365;
        return true;
      });
    }

    // 위험 계정만 보기 — API Status 기준(위험·주의)
    if (showRiskyOnly) {
      filtered = filtered.filter((c) => {
        const st = String(c.status || '').trim();
        if (st === '위험' || st === '주의') return true;
        const rate = Number(c.usageRatePercent ?? c.renewalRate ?? 0);
        const diffDays = getContractRemainingDays(c);
        if (diffDays !== null && diffDays < 0) return false;
        return rate < 30 || (diffDays !== null && diffDays <= 30 && rate < 20);
      });
    }

    return filtered;
  }, [
    contractList,
    searchText,
    probabilityFilter,
    expiryFilter,
    showRiskyOnly,
  ]);

  // ag-grid rowData 설정
  const rowData = useMemo(() => {
    return filteredContracts.map((contract, idx) => ({
      ...contract,
      /** 목록 내 유일해야 함 — companyId가 행마다 중복이면 AG Grid가 행을 누적시킴 */
      _agGridRowId: `cul-${idx}-${String(contract.coCd ?? '')}-${String(contract.companyName ?? '')}`,
    }));
  }, [filteredContracts]);

  // ag-grid rowStyle 설정 (만료 임박 행 강조)
  const getRowStyle = (params) => {
    const contract = params.data;
    if (!contract) return null;

    const daysRemaining = getContractRemainingDays(contract);

    const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30;

    if (isExpiringSoon) {
      return { backgroundColor: 'rgba(185, 28, 28, 0.04)' };
    }
    return null;
  };

  const displayedRowCount = useMemo(() => {
    if (rowData.length === 0) return 1;
    const start = gridCurrentPage * gridPageSize;
    const remaining = rowData.length - start;
    return Math.min(gridPageSize, Math.max(remaining, 0)) || 1;
  }, [rowData.length, gridPageSize, gridCurrentPage]);

  const gridViewportHeight =
    CUL_GRID_HEADER_HEIGHT +
    CUL_GRID_ROW_HEIGHT * displayedRowCount +
    CUL_GRID_PAGINATION_HEIGHT;

  useEffect(() => {
    setGridCurrentPage(0);
    gridRef.current?.api?.paginationGoToFirstPage?.();
  }, [rowData]);

  const controlSurfaceSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: theme.surface,
      borderRadius: 1,
      fontSize: 14,
      '& fieldset': { borderColor: 'rgba(15, 23, 42, 0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(15, 23, 42, 0.2)' },
      '&.Mui-focused fieldset': {
        borderColor: `${theme.primary.main} !important`,
        borderWidth: '1px',
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        py: { xs: 2.5, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        background: theme.pageBg,
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, maxWidth: 720 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.primary.main,
                color: '#fff',
                flexShrink: 0,
                boxShadow: theme.shadow.sm,
              }}
            >
              <BusinessCenter sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: theme.text.muted,
                  textTransform: 'uppercase',
                  mb: 0.75,
                }}
              >
                계약 · 고객 관리
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.35rem', md: '1.6rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: theme.text.primary,
                  lineHeight: 1.25,
                }}
              >
                계약 및 리스크 관리
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  color: theme.text.secondary,
                  fontSize: 14,
                  lineHeight: 1.65,
                  fontWeight: 400,
                }}
              >
                관리자가 고객사 계약을 등록·조회하고, 만료·갱신·위험 지표를 한
                화면에서 점검할 수 있습니다.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={onExport}
              sx={{
                borderColor: 'rgba(148,163,184,0.3)',
                color: 'text.primary',
                fontWeight: 700,
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(19,127,236,0.04)',
                },
              }}
            >
              보고서 내보내기
            </Button> */}
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onAddContract}
              sx={{
                bgcolor: '#137fec',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(19,127,236,0.2)',
                '&:hover': {
                  bgcolor: '#0f6dd1',
                  boxShadow: '0 4px 12px rgba(19,127,236,0.3)',
                },
              }}
            >
              새 계약 추가
            </Button>
          </Box>
        </Box>

        {/* KPI 카드 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {cards.map((c) => (
            <Grid key={c.label} item xs={12} sm={6} lg={3}>
              <Card
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: '1px solid rgba(231,237,243,1)',
                  boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(16,24,40,0.08)',
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    p: 2,
                    opacity: 0.1,
                    '&:hover': { opacity: 0.2 },
                    transition: 'opacity 0.2s',
                  }}
                >
                  {c.icon}
                </Box>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  {c.label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 900 }}>
                    {c.value}
                  </Typography>
                  <Chip
                    label={c.trendText}
                    size="small"
                    icon={
                      c.trendText.startsWith('+') ? (
                        <TrendingUp sx={{ fontSize: 14 }} />
                      ) : c.trendText.startsWith('-') ? (
                        <TrendingDown sx={{ fontSize: 14 }} />
                      ) : (
                        <Remove sx={{ fontSize: 14 }} />
                      )
                    }
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: c.trendBg,
                      color: c.trendColor,
                      '& .MuiChip-icon': {
                        fontSize: 14,
                        marginLeft: 0.5,
                      },
                    }}
                  />
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* 검색 및 필터 */}
        <Card
          sx={{
            p: 2,
            borderRadius: 3,
            border: '1px solid rgba(231,237,243,1)',
            boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', lg: 'center' },
            }}
          >
            <TextField
              fullWidth
              placeholder="업체명 또는 ID로 검색..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                // ag-grid quickFilter 적용
                if (gridRef.current?.api) {
                  gridRef.current.api.setQuickFilter(e.target.value);
                }
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { lg: '33%' },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(248,250,252,1)',
                  '&:hover': {
                    bgcolor: 'rgba(241,245,249,1)',
                  },
                },
              }}
            />
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                flex: 1,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={probabilityFilter}
                  onChange={(e) => setProbabilityFilter(e.target.value)}
                  sx={{
                    bgcolor: 'background.paper',
                  }}
                >
                  <MenuItem value="all">모든 확률</MenuItem>
                  <MenuItem value="high">높음 (&gt;70%)</MenuItem>
                  <MenuItem value="medium">중간 (30-70%)</MenuItem>
                  <MenuItem value="low">낮음 (&lt;30%)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={expiryFilter}
                  onChange={(e) => setExpiryFilter(e.target.value)}
                  sx={{
                    bgcolor: 'background.paper',
                  }}
                >
                  <MenuItem value="all">모든 만료 기간</MenuItem>
                  <MenuItem value="30days">향후 30일</MenuItem>
                  <MenuItem value="quarter">다음 분기</MenuItem>
                  <MenuItem value="year">올해</MenuItem>
                </Select>
              </FormControl>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  ml: { lg: 2 },
                  pl: { lg: 2 },
                  borderLeft: { lg: '1px solid rgba(231,237,243,1)' },
                }}
              >
                <Typography
                  sx={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' }}
                >
                  위험 계정만 보기
                </Typography>
                <Switch
                  checked={showRiskyOnly}
                  onChange={(e) => setShowRiskyOnly(e.target.checked)}
                  size="small"
                />
              </Box>
            </Box>
          </Box>
        </Card>

        <ContractStatusLegendPanel />

        <Typography
          sx={{
            mb: 1.5,
            fontSize: 12,
            color: 'text.secondary',
            fontWeight: 600,
          }}
        >
          행을 더블클릭하거나 오른쪽 「상세 보기」 버튼으로 회사별 사용 분석 화면을
          열 수 있습니다.
        </Typography>

        {/* 계약 목록 테이블 - ag-grid */}
        <Card
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(231,237,243,1)',
            boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
            overflow: 'hidden',
            '& .cul-basic-contract-grid .ag-body-viewport': {
              overflowY: 'hidden',
            },
          }}
        >
          <div
            className="ag-theme-alpine cul-basic-contract-grid"
            style={{
              width: '100%',
              height: rowData.length === 0 ? 200 : gridViewportHeight,
            }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              getRowId={(params) => String(params.data?._agGridRowId ?? '')}
              getRowStyle={getRowStyle}
              rowHeight={CUL_GRID_ROW_HEIGHT}
              headerHeight={CUL_GRID_HEADER_HEIGHT}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
              }}
              pagination={true}
              paginationPageSize={gridPageSize}
              paginationPageSizeSelector={[10, 25, 50, 100]}
              onGridReady={(params) => {
                const api = params.api;
                if (!api) return;
                setGridPageSize(api.paginationGetPageSize());
                setGridCurrentPage(api.paginationGetCurrentPage());
              }}
              onPaginationChanged={(params) => {
                const api = params.api;
                if (!api) return;
                const size = api.paginationGetPageSize();
                const page = api.paginationGetCurrentPage();
                if (size !== gridPageSize) setGridPageSize(size);
                if (page !== gridCurrentPage) setGridCurrentPage(page);
              }}
              localeText={{
                page: '페이지',
                more: '더보기',
                to: '~',
                of: '/',
                next: '다음',
                last: '마지막',
                first: '첫',
                previous: '이전',
                loadingOoo: '로딩 중...',
                noRowsToShow: '데이터가 없습니다',
                pageSizeSelectorLabel: '페이지 크기:',
              }}
              animateRows={true}
              rowSelection="single"
              suppressCellFocus={true}
              suppressRowClickSelection={true}
              onRowDoubleClicked={(event) => {
                if (event?.data) {
                  handleViewDetailInternal(event.data);
                }
              }}
            />
          </div>
        </Card>
      </Box>

      {/* 회사 상세 대시보드 모달 */}
      <CompanyDetailModal
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        companyData={selectedCompany}
        detailData={companyDetailData}
        onEditContract={onEditContract}
      />
    </Box>
  );
}

export default CUL_BASIC;
